// src/modules/app-views/invoices/components/InvoicePreviewHeader.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Space, Typography, QRCode, Divider, Tooltip, theme } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import useZustand from 'hooks/useZustand';
import app from 'configs/ConfigIndex';
import CardHeaderInvoiceTags from './CardHeaderInvoiceTags';
import dayjs from 'dayjs';
import { isCpfOrCnpj, parseCpfToDisplay, parseCnpjToDisplay } from 'utils/helpers';
import { DEFAULT_BACK_COLOR_ACCENT, DEFAULT_BACK_COLOR_ACCENT_DARK } from 'constants/Constants';
import appService from 'services/appService';

const { Text, Title } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const AVATAR_SIZE = 120;
// Background image path (same pattern as CardHeaderBackground)
const BG_IMAGE_PATH = '/img/others/bg-applekkus.jpg';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvoicePreviewHeader = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { selectedDocument } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDarkMode = currentTheme === 'dark';
  const baseColor = app?.baseColor || '#f97316';

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  if (!selectedDocument) return null;

  const recipient = selectedDocument?.recipient || selectedDocument?.recipientEntity;
  const isAuthorized = selectedDocument?.status === 'authorized';
  const accessKey = selectedDocument?.accessKey;

  // Get QR Code image from avatar (real QR Code from fiscal provider - same as FiscalInfoSection)
  const qrCodeImage =
    selectedDocument?.avatar?.storageUrl ||
    selectedDocument?.avatar?.thumbUrl ||
    selectedDocument?.avatar?.base64Thumb;

  // Recipient name and document
  const recipientName =
    recipient?.name ||
    recipient?.fullName ||
    (selectedDocument?.isFinalConsumer ? translateX('consumer') : translateX('no_recipient'));

  const cpfCnpj = recipient?.cpfCnpj || recipient?.cpf?.value || recipient?.cnpj?.value;
  const cpfCnpjFormatted = cpfCnpj
    ? isCpfOrCnpj(cpfCnpj) === 'cpf'
      ? `CPF: ${parseCpfToDisplay(cpfCnpj)}`
      : `CNPJ: ${parseCnpjToDisplay(cpfCnpj)}`
    : null;

  // Location
  const city = recipient?.address?.city || selectedDocument?.issuer?.address?.city;
  const state = recipient?.address?.region || selectedDocument?.issuer?.address?.region;
  const location = city && state ? `${city} - ${state}` : city || state || '';

  // Totals
  const total = selectedDocument?.totals?.documentAmount;
  const issueDate = selectedDocument?.issueDate;
  const documentNumber = selectedDocument?.documentNumber;
  const documentSerie = selectedDocument?.documentSerie;

  // Avatar background color
  const avatarBackColor = isDarkMode ? DEFAULT_BACK_COLOR_ACCENT_DARK : DEFAULT_BACK_COLOR_ACCENT;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Background Header Area */}
      <div
        style={{
          position: 'relative',
          height: 140, // BACKGROUND_HEIGHT + AVATAR_SIZE / 2,
          // marginBottom: AVATAR_SIZE / 2 + 8,
        }}
      >
        {/* Colored Background with Image - matches CardHeaderBackground style */}
        <div
          style={{
            position: 'absolute',
            top: -24,
            left: -24,
            right: -24,
            height: 80, // BACKGROUND_HEIGHT + 24,
            backgroundColor: baseColor,
            backgroundImage: `url(${BG_IMAGE_PATH})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center bottom',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Avatar Container - centered, overlapping background */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            // top: BACKGROUND_HEIGHT - AVATAR_SIZE / 2,
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              borderRadius: 8,
              background: avatarBackColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${isDarkMode ? '#555' : '#d9d9d9'}`,
              overflow: 'hidden',
            }}
          >
            {/* Priority: 1) Avatar QR image, 2) Generated QRCode from accessKey, 3) Placeholder */}
            {qrCodeImage ? (
              <img
                src={qrCodeImage}
                alt="QR Code"
                style={{
                  width: AVATAR_SIZE - 16,
                  height: AVATAR_SIZE - 16,
                  objectFit: 'contain',
                  padding: 4,
                }}
              />
            ) : isAuthorized && accessKey ? (
              <QRCode
                value={accessKey}
                size={AVATAR_SIZE - 16}
                bordered={false}
                style={{ padding: 4 }}
              />
            ) : (
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: isDarkMode ? '#555' : '#bfbfbf',
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  userSelect: 'none',
                }}
              >
                {translateX('qrcode')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content below avatar */}
      <div style={{ textAlign: 'center' }}>
        {/* Tags: Validation + Status + Type + Operation */}
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
          <CardHeaderInvoiceTags data={selectedDocument} />
        </div>

        <Divider style={{ margin: '24px 0' }} />

        {/* Recipient Name */}
        <Title
          level={4}
          // style={{ margin: '24px 0 4px' }}
        >
          {recipientName}
        </Title>

        {/* CPF/CNPJ | Location */}
        <Text type="secondary" style={{ fontSize: 13 }}>
          {/*{[cpfCnpjFormatted, location].filter(Boolean).join(' | ')}*/}
          {cpfCnpjFormatted}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: 13 }}>
          {location}
        </Text>

        {/* Document Info Row */}
        <div style={{ marginTop: 16 }}>
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            {/* Document Number - with copy (copies accessKey) */}
            {documentNumber && (
              <InfoRowWithCopy
                label={translateX('number')}
                value={`${documentNumber}-${documentSerie || 1}`}
                copyValue={documentNumber}
                copyMessage="data_copied"
                strong
                colorPrimary={token.colorPrimary}
              />
            )}

            {/* Access Key - with copy, ellipsis, and link to SEFAZ consultation */}
            {/* Priority: 1) urls.qrCode from backend, 2) urls.consult, 3) generated fallback */}
            {accessKey && (
              <InfoRowWithCopy
                label={translateX('key')}
                value={accessKey}
                copyMessage="fiscal_key_copied"
                ellipsis
                externalLink={
                  selectedDocument?.urls?.qrCode ||
                  selectedDocument?.urls?.consult ||
                  buildFiscalConsultLink(accessKey, selectedDocument?.environment)
                }
                externalLinkTooltip={translateX('consult_at_sefaz')}
                colorPrimary={token.colorPrimary}
              />
            )}

            {/* Issue Date */}
            <InfoRow
              label={translateX('emission')}
              value={issueDate ? dayjs(issueDate).format('DD/MM/YYYY') : null}
            />

            {/* Total */}
            <InfoRow
              label={translateX('total')}
              value={total != null ? financialService.formatCurrency(total) : null}
              strong
            />
          </Space>
        </div>

        <Divider style={{ margin: '24px 0' }} />
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicePreviewHeader;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Info row component
 */
function InfoRow({ label, value, strong = false }) {
  if (!value) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text type="secondary">{label}:</Text>
      <Text strong={strong}>{value}</Text>
    </div>
  );
}

/**
 * Info row component with copy to clipboard
 * Copy icon is placed to the LEFT of the value
 * If copyValue is not provided, copies the value itself
 * @param {boolean} ellipsis - Truncate value with ellipsis if too long
 * @param {string} externalLink - URL for external link icon (opens in new tab)
 * @param {string} externalLinkTooltip - Tooltip text for external link icon
 */
function InfoRowWithCopy({
  label,
  value,
  copyValue,
  copyMessage,
  strong = false,
  ellipsis = false,
  externalLink,
  externalLinkTooltip,
  colorPrimary,
}) {
  if (!value) return null;

  const handleCopy = () => {
    const textToCopy = copyValue || value;
    appService.handleCopyToClipboard(textToCopy, copyMessage);
  };

  const handleOpenLink = () => {
    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <Text type="secondary" style={{ flexShrink: 0 }}>
        {label}:
      </Text>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
          maxWidth: 120,
        }}
      >
        {ellipsis ? (
          <Tooltip title={value}>
            <Text
              strong={strong}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Text>
          </Tooltip>
        ) : (
          <Text strong={strong}>{value}</Text>
        )}

        <Tooltip title={translateX('copy')}>
          <CopyOutlined
            style={{ cursor: 'pointer', color: colorPrimary, fontSize: 12, flexShrink: 0 }}
            onClick={handleCopy}
          />
        </Tooltip>

        {externalLink && (
          <Tooltip title={externalLinkTooltip || translateX('open_in_new_tab')}>
            <LinkOutlined
              style={{ cursor: 'pointer', color: colorPrimary, fontSize: 12, flexShrink: 0 }}
              onClick={handleOpenLink}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/**
 * Build the URL for consulting a fiscal document at SEFAZ portal
 * IMPORTANT: This is a FALLBACK only. The correct URL should come from the backend
 * in document.urls.qrCode (which includes the verification hash from SEFAZ).
 * This fallback URL may not work for homologation environments.
 *
 * Uses the access key to determine if it's NFe (model 55) or NFCe (model 65)
 * @param {string} accessKey - 44-digit access key
 * @param {string} environment - 'production' or 'homologation'
 * @returns {string} URL for SEFAZ consultation (fallback, may not work for homologation)
 */
function buildFiscalConsultLink(accessKey, environment) {
  if (!accessKey || accessKey.length !== 44) return null;

  // Extract document model from access key (positions 21-22, 0-indexed: 20-21)
  // 55 = NFe, 65 = NFCe
  const documentModel = accessKey.substring(20, 22);

  // Note: For homologation, this fallback URL won't work because:
  // 1. Each state has its own homologation URL
  // 2. The QR code URL includes a verification hash not present in the access key
  // The backend should save the correct URL in document.urls.qrCode

  if (documentModel === '65') {
    // NFCe - Portal Nacional do Consumidor (production only)
    return `https://www.nfce.fazenda.gov.br/portal/consultarNFCe.aspx?chNFe=${accessKey}`;
  }

  // NFe - Portal Nacional da NFe
  return `https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=completa&tipoConteudo=XbSeqxE8pl8=`;
}
